/**
 * Централизованная карта React Query keys.
 *
 * Все queryKey приложения — здесь. Это позволяет:
 *   • точечно инвалидировать после мутации:
 *     `qc.invalidateQueries({queryKey: keys.clients.detail(id)})`
 *   • инвалидировать весь домен:
 *     `qc.invalidateQueries({queryKey: keys.clients.all})`
 *   • поймать в одном месте если ключи разъезжаются
 *
 * Структура повторяет платформенную зону из ADMIN_BACKEND_SPEC §3.
 * Request-типы импортируем из сервисов (тип реквеста — часть ключа).
 */

import type {GetAccountsRequest} from '@services/accounts';
import type {GetClientsRequest} from '@services/clients';
import type {GetJobRunsRequest} from '@services/system';

export const keys = {
  auth: {
    all: ['auth'] as const,
  },

  dashboard: {
    all: ['dashboard'] as const,
    summary: (period: string) => [...keys.dashboard.all, 'summary', period] as const,
    trends: (months: number) => [...keys.dashboard.all, 'trends', months] as const,
  },

  clients: {
    all: ['clients'] as const,
    list: (req: GetClientsRequest) => [...keys.clients.all, 'list', req] as const,
    detail: (id: string) => [...keys.clients.all, 'detail', id] as const,
    staff: (id: string) => [...keys.clients.all, 'staff', id] as const,
    statusCounts: () => [...keys.clients.all, 'status-counts'] as const,
    cities: () => [...keys.clients.all, 'cities'] as const,
    phoneCheck: (digits: string) => [...keys.clients.all, 'phone-check', digits] as const,
    specializations: () => [...keys.clients.all, 'specializations'] as const,
  },

  tariffs: {
    all: ['tariffs'] as const,
    current: () => [...keys.tariffs.all, 'current'] as const,
    history: (type: 'solo' | 'clinic') => [...keys.tariffs.all, 'history', type] as const,
  },

  trial: {
    all: ['trial'] as const,
    features: () => [...keys.trial.all, 'features'] as const,
  },

  payments: {
    all: ['payments'] as const,
    list: (req: Record<string, unknown>) => [...keys.payments.all, 'list', req] as const,
  },

  /** Лицевой счёт клиники — остаток и журнал операций. */
  ledger: {
    all: ['ledger'] as const,
    list: (clinicId: string, page: number) => [...keys.ledger.all, clinicId, page] as const,
    /** Весь счёт одной клиники — инвалидируем после ручной операции. */
    client: (clinicId: string) => [...keys.ledger.all, clinicId] as const,
  },

  applications: {
    all: ['applications'] as const,
    list: (req: Record<string, unknown>) => [...keys.applications.all, 'list', req] as const,
    detail: (id: string) => [...keys.applications.all, 'detail', id] as const,
  },

  audit: {
    all: ['audit'] as const,
    list: (req: Record<string, unknown>) => [...keys.audit.all, 'list', req] as const,
  },

  reports: {
    all: ['reports'] as const,
    clinics: (period: string) => [...keys.reports.all, 'clinics', period] as const,
    summary: (id: string, period: string) => [...keys.reports.all, 'summary', id, period] as const,
    revenue: (id: string, period: string) => [...keys.reports.all, 'revenue', id, period] as const,
    doctors: (id: string, period: string) => [...keys.reports.all, 'doctors', id, period] as const,
    services: (id: string, period: string) =>
      [...keys.reports.all, 'services', id, period] as const,
  },

  accounts: {
    all: ['accounts'] as const,
    list: (req: GetAccountsRequest) => [...keys.accounts.all, 'list', req] as const,
  },

  usage: {
    all: ['usage'] as const,
    platform: (period: string) => [...keys.usage.all, 'platform', period] as const,
    clinic: (id: string, from: string, to: string) =>
      [...keys.usage.all, 'clinic', id, from, to] as const,
  },

  system: {
    all: ['system'] as const,
    worker: () => [...keys.system.all, 'worker'] as const,
    runs: (req: GetJobRunsRequest) => [...keys.system.all, 'runs', req] as const,
    telemetry: (days: number) => [...keys.system.all, 'telemetry', days] as const,
  },

  /** Общие шаблоны WhatsApp платформы — одни на все клиники. */
  templates: {
    all: ['templates'] as const,
    list: () => [...keys.templates.all, 'list'] as const,
  },

  refs: {
    all: ['refs'] as const,
    cities: () => [...keys.refs.all, 'cities'] as const,
    specializations: () => [...keys.refs.all, 'specializations'] as const,
  },
} as const;
